/**
 * Customizable Employee Dashboard
 * Phase 1: Common widgets with flexible grid layout
 * Created: January 29, 2026
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RefreshCw, Settings, LayoutGrid, Users } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  WIDGET_COMPONENTS,
  WIDGET_ENDPOINTS
} from '../components/dashboard/DashboardWidgets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function EmployeeDashboardPage() {
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [widgetData, setWidgetData] = useState({});
  const [widgetLoading, setWidgetLoading] = useState({});
  const [widgetErrors, setWidgetErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [absentees, setAbsentees] = useState({ count: 0, absentees: [] });

  // Fetch dashboard configuration
  const fetchDashboardConfig = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/dashboard/config/me`,
        { headers: getAuthHeader() }
      );
      setDashboardConfig(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard config:', error);
      // Return default config
      return {
        layout: {
          columns: 3,
          widgets: [
            { widget_id: 'my_tasks_scores', position: { row: 0, col: 0 }, size: { width: 2, height: 2 }, enabled: true },
            { widget_id: 'action_required', position: { row: 0, col: 2 }, size: { width: 1, height: 2 }, enabled: true },
            { widget_id: 'todays_celebrations', position: { row: 2, col: 0 }, size: { width: 1, height: 1 }, enabled: true },
            { widget_id: 'todays_announcements', position: { row: 2, col: 1 }, size: { width: 2, height: 1 }, enabled: true }
          ]
        }
      };
    }
  }, []);

  // Fetch data for a single widget
  const fetchWidgetData = useCallback(async (widgetId) => {
    const endpoint = WIDGET_ENDPOINTS[widgetId];
    if (!endpoint) return;

    setWidgetLoading(prev => ({ ...prev, [widgetId]: true }));
    setWidgetErrors(prev => ({ ...prev, [widgetId]: null }));

    try {
      const response = await axios.get(
        `${API_URL}${endpoint}`,
        { headers: getAuthHeader() }
      );
      setWidgetData(prev => ({ ...prev, [widgetId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch widget ${widgetId}:`, error);
      setWidgetErrors(prev => ({ 
        ...prev, 
        [widgetId]: error.response?.data?.detail || 'Failed to load' 
      }));
    } finally {
      setWidgetLoading(prev => ({ ...prev, [widgetId]: false }));
    }
  }, []);

  // Fetch all widget data
  const fetchAllWidgetData = useCallback(async (config) => {
    const enabledWidgets = config?.layout?.widgets?.filter(w => w.enabled) || [];
    
    await Promise.all(
      enabledWidgets.map(widget => fetchWidgetData(widget.widget_id))
    );
  }, [fetchWidgetData]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const config = await fetchDashboardConfig();
      setDashboardConfig(config);
      await fetchAllWidgetData(config);
      // Fetch absentees
      try {
        const res = await axios.get(`${API_URL}/api/employee-requests/todays-absentees`, { headers: getAuthHeader() });
        setAbsentees(res.data || { count: 0, absentees: [] });
      } catch (e) { /* ignore */ }
      setLoading(false);
      setLastRefresh(new Date());
    };
    init();
  }, [fetchDashboardConfig, fetchAllWidgetData]);

  // Refresh all widgets
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllWidgetData(dashboardConfig);
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  // Render a single widget
  const renderWidget = (widgetConfig) => {
    const { widget_id, size } = widgetConfig;
    const WidgetComponent = WIDGET_COMPONENTS[widget_id];
    
    if (!WidgetComponent) {
      return (
        <Card className="h-full">
          <CardContent className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Widget not found: {widget_id}</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <WidgetComponent
        data={widgetData[widget_id]}
        loading={widgetLoading[widget_id]}
        error={widgetErrors[widget_id]}
      />
    );
  };

  // Calculate grid styles for a widget
  const getWidgetGridStyle = (widgetConfig) => {
    const { position, size } = widgetConfig;
    return {
      gridColumn: `span ${size?.width || 1}`,
      gridRow: `span ${size?.height || 1}`,
      minHeight: `${(size?.height || 1) * 180}px`
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const enabledWidgets = dashboardConfig?.layout?.widgets?.filter(w => w.enabled) || [];
  const columns = dashboardConfig?.layout?.columns || 3;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="employee-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">My Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Today's Absentees */}
        {absentees.count > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors cursor-pointer border border-gray-200">
                <Users className="w-3.5 h-3.5" />
                Today&apos;s Absentees: {absentees.count}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" align="start">
              <p className="text-sm font-semibold mb-2">Today&apos;s Absentees</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {absentees.absentees.map((a, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                    <span className="font-medium">{a.name}</span>
                    <span className="text-muted-foreground">{a.type}</span>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Widget Grid */}
        {enabledWidgets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No widgets configured</h3>
              <p className="text-muted-foreground mb-4">
                Ask your administrator to configure your dashboard.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div 
            className="grid gap-4"
            style={{ 
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
            }}
            data-testid="widget-grid"
          >
            {enabledWidgets
              .sort((a, b) => {
                // Sort by row first, then by column
                if (a.position.row !== b.position.row) {
                  return a.position.row - b.position.row;
                }
                return a.position.col - b.position.col;
              })
              .map((widgetConfig, index) => (
                <div
                  key={widgetConfig.widget_id || index}
                  style={getWidgetGridStyle(widgetConfig)}
                  data-testid={`widget-${widgetConfig.widget_id}`}
                >
                  {renderWidget(widgetConfig)}
                </div>
              ))}
          </div>
        )}

        {/* Debug Info (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <Card className="mt-4 bg-gray-50">
            <CardHeader className="py-2">
              <CardTitle className="text-sm text-muted-foreground">Debug Info</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              <p>Columns: {columns}</p>
              <p>Widgets: {enabledWidgets.length}</p>
              <p>Config ID: {dashboardConfig?.id || 'default'}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
